import { AfterViewInit, Component, ElementRef, EventEmitter, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { environment } from '../../../../environments/environment';

/** How long to wait for Cloudflare's script before offering a retry. */
export const TURNSTILE_LOAD_TIMEOUT_MS = 15000;

/**
 * Reusable Cloudflare Turnstile challenge widget.
 *
 * Renders a Turnstile widget and emits the resulting token via (tokenChange).
 * The token MUST be verified server-side (see middlewares/turnstile.js) — the widget
 * alone is not protection. Emits '' when the challenge expires, errors, or is reset.
 *
 * When TURNSTILE_SITE_KEY is not configured the component renders nothing and stays
 * inert (`enabled === false`), so forms keep working until keys are provisioned.
 *
 * Usage:
 *   <app-turnstile #challenge (tokenChange)="token = $event"></app-turnstile>
 *   // gate submit on: !challenge.enabled || !!token
 *   // after submit:   challenge.reset()
 */
@Component({
  selector: 'app-turnstile',
  template: `
    <div #container class="turnstile-container"></div>
    <p *ngIf="loadFailed" class="turnstile-failed">
      The security check didn't load.
      <button mat-button type="button" color="primary" (click)="retry()">Try again</button>
    </p>
  `,
  // The widget is a fixed-width iframe injected by Turnstile, so it sits hard against the
  // left edge by default. :host must be block for the container to span the available width
  // and have something to centre within.
  styles: [`
    :host {
      display: block;
    }

    .turnstile-container {
      display: flex;
      justify-content: center;
    }

    .turnstile-failed {
      text-align: center;
    }
  `],
})
export class TurnstileComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;
  @Output() tokenChange = new EventEmitter<string>();

  /** True only when a site key is configured; parents gate submit on this. */
  readonly enabled = !!environment.TURNSTILE_SITE_KEY;

  /** Cloudflare's script failed or timed out; the user is offered a retry. */
  loadFailed = false;

  private widgetId: string | null = null;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    if (!this.enabled) return;
    this.load();
  }

  retry(): void {
    this.loadFailed = false;
    this.load();
  }

  private load(): void {
    this.loadScript()
      .then(() => this.render())
      .catch((err) => {
        console.error('Turnstile failed to load', err);
        this.loadFailed = true;
      });
  }

  ngOnDestroy(): void {
    this.remove();
  }

  /** Clears the solved token and asks the widget for a fresh challenge (call after submit). */
  reset(): void {
    const turnstile = (window as any).turnstile;
    if (turnstile && this.widgetId) {
      try { turnstile.reset(this.widgetId); } catch { /* widget already gone */ }
    }
    this.tokenChange.emit('');
  }

  private render(): void {
    const turnstile = (window as any).turnstile;
    if (!turnstile || !this.container?.nativeElement) return;
    this.remove();
    this.widgetId = turnstile.render(this.container.nativeElement, {
      sitekey: environment.TURNSTILE_SITE_KEY,
      callback: (token: string) => this.ngZone.run(() => this.tokenChange.emit(token)),
      'error-callback': () => this.ngZone.run(() => this.tokenChange.emit('')),
      'expired-callback': () => this.ngZone.run(() => this.tokenChange.emit('')),
    });
  }

  private remove(): void {
    const turnstile = (window as any).turnstile;
    if (turnstile && this.widgetId) {
      try { turnstile.remove(this.widgetId); } catch { /* widget already gone */ }
    }
    this.widgetId = null;
  }

  /**
   * Waits for Cloudflare's script, reusing a tag another widget already added. A tag that errors
   * or has not loaded within the timeout is removed, so the next attempt starts a fresh download
   * instead of waiting on events that already fired.
   */
  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).turnstile) { resolve(); return; }
      let script = document.getElementById('cf-turnstile-script') as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = 'cf-turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      const tag = script;
      const fail = (reason: string) => {
        clearTimeout(timer);
        tag.remove();
        reject(new Error(reason));
      };
      const timer = setTimeout(() => fail('Timed out loading Turnstile'), TURNSTILE_LOAD_TIMEOUT_MS);
      tag.addEventListener('load', () => { clearTimeout(timer); resolve(); });
      tag.addEventListener('error', () => fail('Failed to load Turnstile'));
    });
  }
}
